import { useGetJson } from '../../../infrastructure/api/useGetJson';
import { useDashboardFilters } from '../DashboardFiltersContext';
import React, { useEffect, useState } from 'react';
import { DashboardComponentApiRequestStateWrapper } from '../DashboardComponentApiRequestStateWrapper';
import { useInternationalisation } from '../../../internationalisation/hooks/useInternationalisation';
import { useDevice } from '../../../infrastructure/hooks/useDevice';
import { ChartDataPoint } from '../../../infrastructure/charts/Chart';
import { DashboardComponentLayout } from '../DashboardComponentLayout';
import { StackedBarChart } from '../../../infrastructure/charts/StackedBarChart';
import { ComponentResponse } from '../DashboardComponent';

export const SecuritiesUnrealisedComponentName = 'Securities Unrealised P/L';

export const SecuritiesUnrealised = () => {
  const endpointUrl = 'api/dashboards/GetDataForSecuritiesPLUnrealisedChart';
  const getRequest = useGetJson<
    GetDataForSecuritiesUnrealisedQuery,
    GetDataForSecuritiesUnrealisedResponse
  >(endpointUrl);

  const { companyId, fromDate } = useDashboardFilters();

  const makeRequest = () => {
    if (companyId != null && fromDate != null) {
      getRequest.makeRequest({
        queryParameters: {
          companyId,
          runDate: fromDate,
        },
      });
    }
  };

  useEffect(() => {
    makeRequest();
  }, [companyId, fromDate]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DashboardComponentApiRequestStateWrapper
      apiRequestState={getRequest.state}
      retry={makeRequest}
    >
      {(response, showLoadingOverlay) => (
        <SecuritiesUnrealisedChartComponent
          response={response}
          showLoadingOverlay={showLoadingOverlay}
        />
      )}
    </DashboardComponentApiRequestStateWrapper>
  );
};

type ComponentProps = {
  response: GetDataForSecuritiesUnrealisedResponse;
  showLoadingOverlay: boolean;
};

const SecuritiesUnrealisedChartComponent = ({ response, showLoadingOverlay }: ComponentProps) => {
  const { translate, formatNumber } = useInternationalisation();
  const { isMobile } = useDevice();

  const graphData: Array<ChartDataPoint> = response.dataPoints.map((dataPoint) => ({
    label: dataPoint.label,
    value: dataPoint.value,
  }));
  return (
    <DashboardComponentLayout
      headerText={translate('pages.dashboard.components.securitiesUnrealised.title')}
      showLoadingOverlay={showLoadingOverlay}
      showNoDataMessage={response.noDataMessage}
    >
      <StackedBarChart
        exportFilename={translate('pages.dashboard.components.securitiesUnrealised.exportName')}
        dataPoints={graphData}
        height="stretch"
        responsiveOptions={{
          condition: {
            callback: () => isMobile,
          },
        }}
        yAxisOptions={{
          labels: {
            formatter() {
              return `${this.value}%`;
            },
          },
        }}
        tooltipOptions={{
          pointFormatter() {
            return `<b><span style="color: ${this.color}">${
              this.name
            }&nbsp;</span>:&nbsp;${formatNumber(this.y ?? 0, { decimalPlaces: 2 })}</b>`;
          },
          headerFormat: `Security type <br/>`,
        }}
      />
    </DashboardComponentLayout>
  );
};

type GetDataForSecuritiesUnrealisedQuery = {
  companyId: number;
  runDate: string;
};

export type GetDataForSecuritiesUnrealisedResponse = ComponentResponse & {
  dataPoints: Array<{
    value: number;
    label: string;
  }>;
};
